import type { ComponentDefinition } from './fixtureApiConsumer.js';
import type { RenderResult, StyleDefinition } from './fixtureApi.js';
import { getDefaultPropertyValues } from './PropertySchema.js';
import { getCachedContentHeight, setCachedContentHeight } from '../lib/fixtureSizeCache.js';

function injectStyles(
	styles: readonly StyleDefinition[],
	targetDoc: Document | ShadowRoot,
): { injectedElements: HTMLElement[]; adoptedSheets: CSSStyleSheet[] } {
	const injectedElements: HTMLElement[] = [];
	const adoptedSheets: CSSStyleSheet[] = [];
	const appendTarget = targetDoc instanceof ShadowRoot ? targetDoc : targetDoc.head;
	for (const style of styles) {
		if (style.type === 'css') {
			const styleEl = document.createElement('style');
			styleEl.textContent = style.content;
			appendTarget.appendChild(styleEl);
			injectedElements.push(styleEl);
		} else if (style.type === 'url') {
			const linkEl = document.createElement('link');
			linkEl.rel = 'stylesheet';
			linkEl.href = style.href;
			appendTarget.appendChild(linkEl);
			injectedElements.push(linkEl);
		} else if (style.type === 'adopted') {
			targetDoc.adoptedStyleSheets = [...targetDoc.adoptedStyleSheets, style.sheet];
			adoptedSheets.push(style.sheet);
		}
	}
	return { injectedElements, adoptedSheets };
}

function removeStyles(
	injectedElements: HTMLElement[],
	adoptedSheets: CSSStyleSheet[],
	targetDoc: Document | ShadowRoot,
): void {
	for (const el of injectedElements) {
		el.remove();
	}
	for (const sheet of adoptedSheets) {
		targetDoc.adoptedStyleSheets = targetDoc.adoptedStyleSheets.filter(s => s !== sheet);
	}
}

export class ComponentRendering {
	readonly renderTarget: HTMLElement;
	readonly iframe: HTMLIFrameElement | null = null;
	private readonly _abortController = new AbortController();
	private _resolvedResult: RenderResult | undefined;
	private _injectedElements: HTMLElement[] = [];
	private _adoptedSheets: CSSStyleSheet[] = [];
	private _styleTarget: Document | ShadowRoot = document;
	private readonly _rendered: Promise<RenderResult | undefined>;

	constructor(host: HTMLElement, readonly component: ComponentDefinition) {
		const isolation = component.isolation;

		if (isolation === 'shadow-dom') {
			const shadowRoot = host.attachShadow({ mode: 'open' });
			this._styleTarget = shadowRoot;
			if (component.styles) {
				const { injectedElements, adoptedSheets } = injectStyles(component.styles, shadowRoot);
				this._injectedElements = injectedElements;
				this._adoptedSheets = adoptedSheets;
			}
			this.renderTarget = document.createElement('div');
			shadowRoot.appendChild(this.renderTarget);
		} else if (isolation === 'iframe') {
			const iframe = document.createElement('iframe');
			iframe.style.border = 'none';
			iframe.style.display = 'block';
			iframe.style.width = '100%';
			iframe.style.height = (getCachedContentHeight(component.id) ?? 0) + 'px';

			const url = new URL(location.pathname, location.origin);
			url.searchParams.set('mode', 'embedded');
			url.searchParams.set('fixture', component.id);
			url.searchParams.set('file', component.sourceFile);
			iframe.src = url.toString();
			host.appendChild(iframe);
			this.iframe = iframe;

			const onMessage = (e: MessageEvent) => {
				if (e.source === iframe.contentWindow && e.data?.type === 'component-explorer:resize') {
					const height = e.data.height;
					iframe.style.height = height + 'px';
					setCachedContentHeight(component.id, height);
				}
			};
			window.addEventListener('message', onMessage);
			this._abortController.signal.addEventListener('abort', () => {
				window.removeEventListener('message', onMessage);
			});

			this.renderTarget = iframe;
			this._rendered = Promise.resolve(undefined);
			return;
		} else {
			if (component.styles) {
				const { injectedElements, adoptedSheets } = injectStyles(component.styles, document);
				this._injectedElements = injectedElements;
				this._adoptedSheets = adoptedSheets;
			}
			this.renderTarget = document.createElement('div');
			host.appendChild(this.renderTarget);
		}

		const props = getDefaultPropertyValues(component.properties);
		const rawResult = component.render(this.renderTarget, {
			props,
			signal: this._abortController.signal,
		});

		if (rawResult instanceof Promise) {
			this._rendered = rawResult.then((resolved) => {
				if (this._abortController.signal.aborted) {
					resolved?.dispose?.();
					return undefined;
				}
				this._resolvedResult = resolved ?? undefined;
				return this._resolvedResult;
			});
		} else {
			this._resolvedResult = (rawResult ?? undefined) as RenderResult | undefined;
			this._rendered = Promise.resolve(this._resolvedResult);
		}
	}

	async waitForRendering(): Promise<RenderResult | undefined> {
		const result = await this._rendered;
		await result?.ready;
		return result;
	}

	dispose(): void {
		this._abortController.abort();
		this._resolvedResult?.dispose?.();
		this._resolvedResult = undefined;
		removeStyles(this._injectedElements, this._adoptedSheets, this._styleTarget);
	}
}
